import { Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { TimeEntrySettings } from 'app/core/user/user.types';
import { roundFixed } from 'app/core/utils/number-utils';
import { NumberFormatter } from '../format/number-formatter';
import { RegionVariantService } from 'app/core/region-variant/region-variant.service';
import { combineLatest, filter, startWith } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TimeUnitsConverterService {
  private settings: TimeEntrySettings;
  private hoursMinutesExpr =
    /^((?<minOnly>\d+(\.[0-9]*)?)|(?<hour>\d+):((?<min>[0-5][0-9](\.[0-9]*)?)|(?<tenMin>[0-5])))$/;

  constructor(
    userService: UserService,
    private numberFormatter: NumberFormatter,
    private regionVariantService: RegionVariantService,
  ) {
    combineLatest({
      user: userService.user$,
      regionVariantChanges: this.regionVariantService.changes$.pipe(startWith(null)),
    })
      .pipe(filter(({ user }) => !!user))
      .subscribe(({ user }) => {
        const settings: TimeEntrySettings = user.settings?.timeEntry || {};
        const defaultTimeEntryMethod = this.regionVariantService.getDefaultTimeEntryMethod();
        const timeEntryMethodOverride = this.regionVariantService.getTimeEntryMethodOverride();
        const timeEntryMethod = timeEntryMethodOverride || settings.timeEntryMethod || defaultTimeEntryMethod;
        const defaultTimeUnitsDigits: number = timeEntryMethod === 'minutes' ? 8 : 2;
        const timeUnitsDigits: number = settings.timeUnitsDigits || defaultTimeUnitsDigits;
        this.settings = {
          ...settings,
          timeEntryMethod: timeEntryMethod,
          timeUnitsDigits: timeUnitsDigits,
        };
      });
  }

  isValid(value: string): boolean {
    if (this.settings.timeEntryMethod === 'decimal' || this.settings.timeEntryMethod === 'conventional') {
      return !isNaN(parseFloat(value));
    } else if (this.settings.timeEntryMethod === 'minutes') {
      return this.hoursMinutesExpr.test(value);
    } else {
      return false;
    }
  }

  parse(value: string | number, forceHours?: boolean): number {
    if (typeof value === 'number') {
      return value;
    }

    const method = forceHours ? 'decimal' : this.settings.timeEntryMethod;

    value = value && value.trim();

    if (!value || !this.isValid(value)) {
      return null;
    }

    let result: number;
    switch (method) {
      default:
        // Default fallthrough to decimal, typescript error if switch incomplete
        const err: never = method;
      case 'decimal':
        result = +value;
        break;
      case 'conventional':
        /* 1 unit is 6 minutes */
        result = (+value * 6) / 60;
        break;
      case 'minutes':
        /* format: h:mm.fraction */
        const { minOnly, hour, min, tenMin } = value.match(this.hoursMinutesExpr).groups;
        result = (+(hour || '0') * 60 + +(minOnly || '0') + +(min || '0') + +(tenMin || '0') * 10) / 60;
        break;
    }

    return this.round(result);
  }

  transform(value: number, forceHours?: boolean): string {
    if (value == null) {
      return '';
    }

    const method = forceHours ? 'decimal' : this.settings.timeEntryMethod;

    switch (method) {
      default:
        // Default fallthrough to decimal, typescript error if switch incomplete
        const err: never = method;
      case 'decimal':
        return this.numberFormatter.format(value, 2);
      case 'conventional':
        return this.numberFormatter.format((value * 60) / 6, 2);
      case 'minutes':
        const hours = Math.floor(value);
        const minutes = roundFixed((value % 1) * 60, 2);
        return hours ? `${hours}:${('' + minutes).padStart(2, '0')}` : `${minutes}`;
    }
  }

  getTimeEntryMethod(): 'decimal' | 'conventional' | 'minutes' {
    return this.settings.timeEntryMethod;
  }
  getTimeUnitsDigits(): number {
    return this.settings.timeUnitsDigits;
  }

  round(value: number): number {
    return roundFixed(value, this.settings.timeUnitsDigits);
  }
}
